import axios from "axios";
import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../../constants/urlConstants";
import { toast,toastConfirm } from "../../../utils/toast";
import {
  Checkbox,
  Form,
  Input,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  AimOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import "./MyAddresses.css";

interface AddressItem {
  id: number;
  msisdn: string;
  name: string;
  pincode: string;
  locality: string;
  address: string;
  city: string;
  state: string;
  landmark: string;
  alternatePhone: string;
  addressType: string;
  latitude: number;
  longitude: number;
  primary: boolean;
}

type Screen = "list" | "map" | "details";

const MAX_ADDRESS   = 3;
const ADDRESS_TYPES = ["HOME", "WORK", "OTHERS"];

// Bengaluru as explicit fallback
const FALLBACK_LAT = 12.9716;
const FALLBACK_LNG = 77.5946;


declare const google: any;

const MyAddresses: FC = (): ReactElement => {
  const [form] = Form.useForm();
  const mapRef          = useRef<HTMLDivElement>(null);
  const mapInstanceRef  = useRef<any>(null);
  const markerRef       = useRef<any>(null);
  const searchInputRef  = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const pageRef         = useRef<HTMLDivElement>(null);
  const mapInitedRef    = useRef(false); // ← prevents double-init
  const [mapHeight, setMapHeight] = useState<number>(0);

  const [screen,       setScreen]       = useState<Screen>("list");
  const [addresses,    setAddresses]    = useState<AddressItem[]>([]);
  const [editing,      setEditing]      = useState<AddressItem | null>(null);
  const [pickedLatLng, setPickedLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [pickedLabel,  setPickedLabel]  = useState<string>("Detecting your location...");
  const [addressType,  setAddressType]  = useState<string>("HOME");
  const [saving,       setSaving]       = useState(false);
  const [locating,     setLocating]     = useState(false);
  const [mapReady,     setMapReady]     = useState(false);

  const userMsisdn = localStorage.getItem("mobile") || "";

  /* ── Fetch saved addresses ── */
  const fetchAddresses = async () => {
    if (!userMsisdn) return;
    try {
      const { data } = await axios.post(`${BASE_URL}/api/address/get`, { msisdn: userMsisdn });
      setAddresses(Array.isArray(data) ? data : data ? [data] : []);
    } catch {
    //  message.error("Failed to fetch addresses");
      toast("error", "Failed to fetch addresses");
    }
  };

  useEffect(() => { fetchAddresses(); }, []);

  const canAddMore = useMemo(() => addresses.length < MAX_ADDRESS, [addresses]);

  /* ── Reverse geocode ── */
  const reverseGeocode = (position: { lat: number; lng: number }) => {
    setPickedLatLng(position);
    if (typeof google === "undefined") return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: position }, (results: any, status: string) => {
      if (status === "OK" && results?.[0]) {
        setPickedLabel(results[0].formatted_address);
      } else {
        setPickedLabel(`${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`);
      }
    });
  };

  /* ── Init Google Map (called ONCE per map screen) ── */
  const initMap = (lat: number, lng: number) => {
    if (!mapRef.current || typeof google === "undefined") return;
    if (mapInitedRef.current) return; // ← guard against double init
    mapInitedRef.current = true;

    const map = new google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 16,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
    });

    const marker = new google.maps.Marker({
      position: { lat, lng },
      map,
      draggable: true,
      animation: google.maps.Animation.DROP,
      icon: { url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png" },
    });

    mapInstanceRef.current = map;
    markerRef.current      = marker;

    marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      reverseGeocode({ lat: pos.lat(), lng: pos.lng() });
    });

    map.addListener("click", (e: any) => {
      const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(pos);
      reverseGeocode(pos);
    });

    reverseGeocode({ lat, lng });
    setMapReady(true);
  };

  /* ── Places Autocomplete ── */
  useEffect(() => {
    if (!mapReady) return;
    let attempts = 0;
    const tryAttach = () => {
      if (!searchInputRef.current || typeof google === "undefined") {
        if (attempts++ < 20) setTimeout(tryAttach, 100);
        return;
      }
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      const ac = new google.maps.places.Autocomplete(searchInputRef.current, {
        componentRestrictions: { country: "in" },
        fields: ["geometry", "formatted_address", "name"],
      });
      autocompleteRef.current = ac;
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place?.geometry?.location) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        mapInstanceRef.current?.setCenter({ lat, lng });
        mapInstanceRef.current?.setZoom(17);
        markerRef.current?.setPosition({ lat, lng });
        reverseGeocode({ lat, lng });
        if (searchInputRef.current) searchInputRef.current.blur();
      });
    };
    tryAttach();
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [mapReady]);

  /* ── Get device location ── */
  const goToCurrentLocation = (onSuccess?: (lat: number, lng: number) => void) => {
    if (!navigator.geolocation) {
      // Device has no geolocation support at all
      if (!mapInitedRef.current) initMap(FALLBACK_LAT, FALLBACK_LNG);
      message.info("Geolocation is not supported on this device.");
      return;
    }

    setLocating(true);
    setPickedLabel("Detecting your location...");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        if (mapInstanceRef.current && markerRef.current) {
          // Map already exists — just pan & update marker
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(17);
          markerRef.current.setPosition({ lat, lng });
          reverseGeocode({ lat, lng });
        } else {
          // First load — init map at real location
          initMap(lat, lng);
        }

        onSuccess?.(lat, lng);
        setLocating(false);
      },
      (err) => {
        setLocating(false);

        if (err.code === err.PERMISSION_DENIED) {
          // User explicitly denied — show toast and ask to use search
          message.warning("Location access denied. Please search for your area using the search bar.");
        } else if (err.code === err.TIMEOUT) {
          message.info("Location detection timed out. Please search or pin your location manually.");
        } else {
          message.info("Could not detect location. Please search or pin manually.");
        }

        // Only init fallback map if map hasn't been inited yet
        if (!mapInitedRef.current) {
          initMap(FALLBACK_LAT, FALLBACK_LNG);
          setPickedLabel("Could not detect location — please pin or search.");
        }
      },
      {
        enableHighAccuracy: true,   // use GPS, not IP/WiFi estimate
        timeout: 10000,             // wait up to 10s
        maximumAge: 0,              // always get a fresh position, never cached
      }
    );
  };

  /* ── Measure map container height ── */
  useEffect(() => {
    if (screen !== "map") return;
    const measure = () => {
      if (!pageRef.current) return;
      const top = pageRef.current.getBoundingClientRect().top;
      setMapHeight(window.innerHeight - top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [screen]);

  /* ── Map screen mount: geolocate OR centre on saved coords ── */
  useEffect(() => {
    if (screen !== "map") return;

    // Reset init guard each time map screen opens
    mapInitedRef.current    = false;
    mapInstanceRef.current  = null;
    markerRef.current       = null;
    setMapReady(false);

    setTimeout(() => {
      if (editing) {
        // Edit mode — centre on the saved coordinates directly
        const lat = editing.latitude  || FALLBACK_LAT;
        const lng = editing.longitude || FALLBACK_LNG;
        initMap(lat, lng);
      } else {
        // Add mode — try real GPS first
        goToCurrentLocation();
      }
    }, 120);
  }, [screen]);

  /* ── Confirm map → fill form ── */
  const confirmMapLocation = () => {
    if (!pickedLatLng) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: pickedLatLng }, (results: any, status: string) => {
      const get = (type: string) =>
        results?.[0]?.address_components?.find((c: any) => c.types.includes(type))?.long_name || "";
      form.setFieldsValue({
        address:   results?.[0]?.formatted_address || pickedLabel,
        locality:  get("sublocality") || get("sublocality_level_1") || get("neighborhood"),
        city:      get("locality"),
        state:     get("administrative_area_level_1"),
        pincode:   get("postal_code"),
        latitude:  pickedLatLng.lat,
        longitude: pickedLatLng.lng,
        msisdn:    userMsisdn,
        addressType,
      });
    });
    setScreen("details");
  };

  /* ── Open Add flow ── */
  const openAdd = () => {
    if (!canAddMore) { message.warning("Maximum 3 addresses allowed"); return; }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ msisdn: userMsisdn });
    setAddressType("HOME");
    setPickedLatLng(null);
    setPickedLabel("Detecting your location...");
    setMapReady(false);
    setScreen("map");
  };

  /* ── Open Edit flow ── */
  const openEdit = (item: AddressItem) => {
    setEditing(item);
    setAddressType(item.addressType || "HOME");
    setPickedLatLng({ lat: item.latitude, lng: item.longitude });
    setPickedLabel(item.address);
    form.setFieldsValue({ ...item, isPrimary: item.primary });
    setMapReady(false);
    setScreen("map");
  };

  /* ── Save address ── */
  const saveAddress = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await axios.post(`${BASE_URL}/api/address/update`, {
          ...editing, ...values, addressType,
        });
      } else {
        await axios.post(`${BASE_URL}/api/address/create`, {
          ...values,
          msisdn:    values.msisdn || userMsisdn,
          addressType,
          isPrimary: values.isPrimary || false,
        });
      }
      await fetchAddresses();
      setScreen("list");
      form.resetFields();
     // message.success(editing ? "Address updated!" : "Address saved!");
      toastConfirm("success", editing ? "Address updated!" : "Address saved!");
    } catch (e: any) {
      if (e?.errorFields) return;
     // message.error("Failed to save address");
      toast("error", "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  /* ── Set primary ── */
  const setPrimary = async (id: number) => {
    try {
      await axios.put(`${BASE_URL}/api/address/${id}/primary`);
      await fetchAddresses();
   //   message.success("Default address updated");
      toast("success", "Default address updated");
    } catch {
      //  message.error("Failed to update default address");
        toast("error", "Failed to update default address");
        }
  };

  /* ── Delete ── */
  const deleteAddress = async (id: number) => {
    try {
      await axios.post(`${BASE_URL}/api/address/delete`, { id, msisdn: userMsisdn });
      await fetchAddresses();
    //  message.success("Address deleted");
      toast("success", "Address deleted");
    } catch {
       // message.error("Failed to delete address");
        toast("error", "Failed to delete address");
        }
  };

  /* ════════════════════════════════
     SCREEN: LIST
  ════════════════════════════════ */
  if (screen === "list") return (
    <div className="ma-page">
      <div className="ma-header">
        <span className="ma-header-title">My Addresses ({addresses.length}/{MAX_ADDRESS})</span>
        <button className="ma-add-btn" onClick={openAdd} disabled={!canAddMore}>
          <PlusOutlined /> Add New Address
        </button>
      </div>
      <div className="ma-content">
        {addresses.length === 0 ? (
          <div className="ma-empty">
            <EnvironmentOutlined className="ma-empty-icon" />
            <p className="ma-empty-title">No saved addresses</p>
            <p className="ma-empty-sub">Add an address for faster checkout</p>
            <button className="ma-add-btn" onClick={openAdd}><PlusOutlined /> Add Address</button>
          </div>
        ) : (
          <div className="ma-list">
            {addresses.map((item) => (
              <div key={item.id} className={`ma-card ${item.primary ? "primary" : ""}`}>
                <div className="ma-card-top">
                  <div className="ma-card-left">
                    <div
                      className={`ma-radio ${item.primary ? "checked" : ""}`}
                      onClick={() => !item.primary && setPrimary(item.id)}
                    >
                      {item.primary && <div className="ma-radio-dot" />}
                    </div>
                  </div>
                  <div className="ma-card-body">
                    <div className="ma-card-name-row">
                      <span className="ma-card-name">{item.name}</span>
                      {item.primary && <span className="ma-badge default">Default</span>}
                      <span className="ma-badge type">{item.addressType}</span>
                    </div>
                    <div className="ma-card-addr">
                      {[item.address, item.locality, item.city, item.state].filter(Boolean).join(", ")}
                      {item.pincode ? ` - ${item.pincode}` : ""}
                    </div>
                    {item.landmark && <div className="ma-card-landmark">Near: {item.landmark}</div>}
                    <div className="ma-card-phone">{item.msisdn}</div>
                  </div>
                </div>
                <div className="ma-card-actions">
                  <button className="ma-action-btn" onClick={() => openEdit(item)}>
                    <EditOutlined /> Edit
                  </button>
                  <span className="ma-action-sep">|</span>
                  <button className="ma-action-btn delete" onClick={() => deleteAddress(item.id)}>
                    <DeleteOutlined /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  /* ════════════════════════════════
     SCREEN: MAP PICKER
  ════════════════════════════════ */
  if (screen === "map") return (
    <div ref={pageRef} className="ma-page ma-page--map" style={mapHeight ? { height: mapHeight } : {}}>
      <div className="ma-header">
        <button className="ma-back-btn" onClick={() => setScreen("list")}>
          <ArrowLeftOutlined />
        </button>
        <span className="ma-header-title">
          {editing ? "Change Location" : "Pin Your Location"}
        </span>
      </div>

      <div className="ma-search-bar">
        <SearchOutlined className="ma-search-icon" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for area, street name..."
          className="ma-search-input"
        />
      </div>

      <div ref={mapRef} className="ma-map" />

      <div className="ma-map-footer">
        <div className="ma-loc-bar">
          <EnvironmentOutlined className="ma-loc-icon" />
          <div className="ma-loc-text">
            <div className="ma-loc-label">Selected location</div>
            <div className="ma-loc-addr">{pickedLabel}</div>
          </div>
        </div>
        <div className="ma-map-btns">
          <button
            className="ma-locate-btn"
            onClick={() => goToCurrentLocation()}
            disabled={locating}
          >
            <AimOutlined /> {locating ? "Locating..." : "Use my current location"}
          </button>
          <button
            className="ma-confirm-btn"
            onClick={confirmMapLocation}
            disabled={!pickedLatLng}
          >
            Confirm location &amp; add details →
          </button>
        </div>
      </div>
    </div>
  );

  /* ════════════════════════════════
     SCREEN: ADDRESS DETAILS FORM
  ════════════════════════════════ */
  return (
    <div className="ma-page">
      <div className="ma-header">
        <button className="ma-back-btn" onClick={() => setScreen("map")}>
          <ArrowLeftOutlined />
        </button>
        <span className="ma-header-title">Address Details</span>
      </div>
      <div className="ma-content">
        {pickedLabel && (
          <div className="ma-autofill-bar">
            <CheckCircleFilled className="ma-autofill-icon" />
            <span>{pickedLabel}</span>
          </div>
        )}
        <Form form={form} layout="vertical" className="ma-form">
          <div className="ma-form-row">
            <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
              <Input placeholder="Full name" size="large" />
            </Form.Item>
            <Form.Item name="msisdn" label="Mobile" rules={[{ required: true, message: "Required" }]} style={{ flex: 1 }}>
              <Input placeholder="Mobile number" size="large" />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="House No, Building, Street" size="large" />
          </Form.Item>
          <div className="ma-form-row">
            <Form.Item name="locality" label="Locality / Area" style={{ flex: 1 }}>
              <Input placeholder="Locality" size="large" />
            </Form.Item>
            <Form.Item name="pincode" label="Pincode" style={{ flex: 1 }}>
              <Input placeholder="Pincode" size="large" />
            </Form.Item>
          </div>
          <div className="ma-form-row">
            <Form.Item name="city" label="City" style={{ flex: 1 }}>
              <Input placeholder="City" size="large" />
            </Form.Item>
            <Form.Item name="state" label="State" style={{ flex: 1 }}>
              <Input placeholder="State" size="large" />
            </Form.Item>
          </div>
          <Form.Item name="landmark" label="Landmark (optional)">
            <Input placeholder="E.g. Near Apollo Hospital" size="large" />
          </Form.Item>
          <Form.Item name="alternatePhone" label="Alternate Phone (optional)">
            <Input placeholder="Alternate phone number" size="large" />
          </Form.Item>
          <div className="ma-type-label">Address Type</div>
          <div className="ma-type-row">
            {ADDRESS_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={`ma-type-btn ${addressType === t ? "active" : ""}`}
                onClick={() => setAddressType(t)}
              >
                {t === "HOME" ? "🏠" : t === "WORK" ? "💼" : "📍"} {t}
              </button>
            ))}
          </div>
          <Form.Item name="isPrimary" valuePropName="checked" initialValue={false}>
            <Checkbox>Set as default address</Checkbox>
          </Form.Item>
          <Form.Item name="latitude"  hidden><Input /></Form.Item>
          <Form.Item name="longitude" hidden><Input /></Form.Item>
          <button
            className="ma-confirm-btn ma-confirm-btn--full"
            type="button"
            onClick={saveAddress}
            disabled={saving}
          >
            {saving ? "Saving..." : editing ? "Update Address" : "Save Address"}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default MyAddresses;